<template>
  <el-row
    align="middle"
    class="barphone"
    :class="{ active: hasCalls }"
    justify="space-around"
    type="flex"
  >
    <AudioTripDialog />
    <CallSomeoneDialog />

    <div class="phone-container">
      <el-button
        :disabled="isOnCall"
        :class="{ disabled: isOnCall }"
        type="text"
        @click="showCallSomeoneDialog"
      >
        <PhoneOutgoingIcon />
        <label class="label-menu-item">Llamar</label>
      </el-button>

      <div class="barphone__phone px-3">
        <el-popover :width="50" popperClass="p-2" trigger="click">
          <el-row>
            <el-col :span="24">
              <el-button v-if="lastCall && !isOnCall" type="text" @click="onCallLastNumber">
                Llamar
              </el-button>
            </el-col>
            <el-col :span="24">
              <el-button v-if="lastCall" type="text" @click="onCreateService">
                Nuevo Servicio
              </el-button>
            </el-col>
            <el-col :span="24">
              <el-button v-if="lastCall" type="text" @click="onClickShowAudio">
                Escuchar grabación
              </el-button>
            </el-col>
          </el-row>
          <el-button slot="reference" class="pr-1" type="text">
            <div
              v-if="lastCall"
              :class="{
                'logged-ext-on-call': isOnCall,
                'logged-ext-not-on-call': !isOnCall,
              }"
            >
              {{ lastCall.callerNumber }}
            </div>
            <div v-else class="no-calls">No se han atendido llamadas</div>
            <label class="label-menu-item call-settings">
              Última atendida
              <span class="ml-1 material-icons m-icon-s">settings</span>
            </label>
          </el-button>
        </el-popover>
      </div>

      <div class="barphone__actions">
        <div v-show="isOnCall" class="px-3 actions-call">
          <el-tooltip v-if="isOnConference" content="Abandonar conferencia">
            <el-popconfirm
              cancelButtonText="No"
              confirmButtonText="Si"
              iconColor="red"
              popperClass="p-2"
              title="¿Quieres abandonar la conferencia?"
              @confirm="onConfirmDropCall"
            >
              <el-button slot="reference" class="pr-1" type="text">
                <PhoneHangupIcon />
              </el-button>
            </el-popconfirm>
          </el-tooltip>

          <el-tooltip v-else content="Colgar llamada">
            <el-popconfirm
              cancelButtonText="No"
              confirmButtonText="Si"
              iconColor="red"
              popperClass="p-2"
              title="¿Quieres colgar la llamada?"
              @confirm="onConfirmHangUp"
            >
              <el-button slot="reference" class="pr-1" type="text">
                <PhoneHangupIcon />
              </el-button>
            </el-popconfirm>
          </el-tooltip>

          <el-tooltip content="Pausar llamada">
            <el-popconfirm
              cancelButtonText="No"
              confirmButtonText="Si"
              iconColor="red"
              popperClass="p-2"
              title="¿Quieres pausar la llamada?"
              @confirm="onConfirmPaused"
            >
              <el-button slot="reference" class="pr-1" type="text">
                <PhonePausedIcon />
              </el-button>
            </el-popconfirm>
          </el-tooltip>

          <el-tooltip content="Aparcar para desviar">
            <el-popconfirm
              cancelButtonText="No"
              confirmButtonText="Si"
              iconColor="red"
              popperClass="p-2"
              title="¿Quieres aparcar para desviar?"
              @confirm="onConfirmForward"
            >
              <el-button slot="reference" class="pr-1" type="text">
                <PhoneForwardIcon />
              </el-button>
            </el-popconfirm>
          </el-tooltip>

          <el-tooltip
            v-if="waitingForMergeCalls.length > 0 && !isOnConference"
            content="Fusionar llamada"
          >
            <el-popover>
              <div>
                <el-button
                  v-for="call in waitingForMergeCalls"
                  :key="call.asteriskId"
                  @click="handleMerge(call)"
                  >{{ call.callerNumber }}</el-button
                >
              </div>
              <el-button slot="reference" class="pr-1" type="text">
                <CallMergeIcon />
              </el-button>
            </el-popover>
          </el-tooltip>
        </div>
      </div>
    </div>

    <phone-extensions />
  </el-row>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { SimpleUser } from "sip.js/lib/platform/web";
import AudioTripDialog from "@/components/AudioTripDialog/index";
import CallSomeoneDialog from "@/components/CallSomeoneDialog/index";
import PhoneHangupIcon from "mdi-vue/PhoneHangup";
import PhonePausedIcon from "mdi-vue/PhonePaused";
import PhoneForwardIcon from "mdi-vue/PhoneForward";
import PhoneOutgoingIcon from "mdi-vue/PhoneOutgoing";
import CallMergeIcon from "mdi-vue/CallMerge";
import ApiClient from "@/api/ApiClient.ts";
import PhoneExtensions from "@/components/navbar/PhoneExtensions";

export default {
  name: "BarPhone",

  components: {
    PhoneExtensions,
    AudioTripDialog,
    CallSomeoneDialog,
    PhoneHangupIcon,
    PhonePausedIcon,
    PhoneForwardIcon,
    PhoneOutgoingIcon,
    CallMergeIcon,
  },

  data() {
    return {
      phoneSIP: null,
    };
  },

  computed: {
    ...mapGetters("app", ["defaultWorkgroup"]),
    ...mapGetters("clientUserRegisterData", ["phoneExt"]),
    ...mapGetters("pbxCalls", [
      "lastCall",
      "hasCalls",
      "isOnCall",
      "waitingForMergeCalls",
      "isOnConference",
    ]),
  },

  methods: {
    ...mapActions("pbxCalls", ["hangUp", "holdCall", "queueTransfer", "joinCalls", "dropCall"]),
    ...mapMutations("clientUserRegisterData", ["setIsSipExternal"]),

    async createSIPPhone() {
      if (!this.phoneExt) return false;

      // SIP over WebSocket Server URL
      // const server = "wss://c0.2kbo.com:9809/ws";
      const server = `wss://${this.defaultWorkgroup.configPhone?.url}:${this.defaultWorkgroup.configPhone?.wssPort}/ws`;

      // SIP Address of Record (AOR)
      // const aor = "sip:61102@c0.2kbo.com:6655";
      const aor = `sip:${this.phoneExt}@${this.defaultWorkgroup.configPhone?.url}:${this.defaultWorkgroup.configPhone?.sipPort}`;

      // SIP Authorization Username
      // const authorizationUsername = "61102"
      const authorizationUsername = this.phoneExt;

      // SIP Authorization Password
      const authorizationPassword = "CYyNC6fq";
      // const authorizationPassword = this.defaultWorkgroup.configPhone.pwd

      // Configuration Options
      const options = {
        aor,
        media: {
          local: {
            audio: document.getElementById("localVideo"),
          },
          remote: {
            audio: document.getElementById("remoteVideo"),
          },
        },
        userAgentOptions: {
          authorizationPassword,
          authorizationUsername,
        },
      };

      // SimpleUser delegate
      this.phoneSIP = new SimpleUser(server, options);

      // Connect to server
      await this.phoneSIP.connect();

      // Register to receive inbound calls (optional)
      await this.phoneSIP.register();

      // Supply delegate to handle inbound calls (optional)
      this.phoneSIP.delegate = {
        onCallReceived: async () => {
          await this.phoneSIP.answer();
          // const response = await this.phoneSIP.answer();
        },
      };
      this.$message({
        type: "success",
        message: "Teléfono SIP Interno conectado",
      });
    },

    onCallLastNumber() {
      this.$store.commit("callSomeoneDialog/setShow", true);
      this.$store.commit("callSomeoneDialog/setPhone", this.lastCall.callerNumber);
    },

    onCreateService() {
      this.$store.commit("clientTrips/setTrip", null);
      const paramId = new Date().getTime();
      this.$router.push({
        path: "/services/create/" + paramId,
        query: {
          callerNumber: this.lastCall.callerNumber,
          asteriskId: this.lastCall.asteriskId,
          wgId: this.lastCall.wgId,
        },
      });
    },

    async onClickShowAudio() {
      let keyAudio = await this.getUrl({
        wgId: this.lastCall.wgId,
        call: this.lastCall.asteriskId,
      });
      if (keyAudio) {
        this.$store.commit("audioTripDialog/setShow", true);
        this.$store.commit("audioTripDialog/setKeyAudio", keyAudio);
      }
    },

    async getUrl(item) {
      const { call, wgId } = item;
      if (call) {
        const { data } = await ApiClient().get("calls", {
          params: {
            wgId: wgId,
            idAsterisk: call,
          },
        });
        if (data?.idAsterisk) {
          return data?.recordPath;
        }
      }
      return "";
    },

    showCallSomeoneDialog() {
      this.$store.commit("callSomeoneDialog/setShow", true);
    },

    async onConfirmHangUp() {
      const payload = {
        asteriskId: this.lastCall?.asteriskId,
      };
      await this.hangUp(payload);
    },

    async onConfirmDropCall() {
      await this.dropCall();
    },

    async onConfirmForward() {
      await this.queueTransfer(this.lastCall?.asteriskId);
      this.$store.dispatch("app/deleteAnsweredCall", this.$route.query.asteriskId);
      this.$router.replace({ path: "/home" });
    },

    async onConfirmPaused() {
      this.$store.dispatch("app/deleteAnsweredCall", this.$route.query.asteriskId);
      await this.holdCall(this.lastCall.asteriskId);
      this.$router.replace({ path: "/home" });
    },

    async handleMerge(call) {
      await this.joinCalls(call.asteriskId);
    },
  },
};
</script>

<style lang="scss" scoped>
.no-calls {
  font-size: 12px;
  height: 20px;
  line-height: 22px;
  color: rgba(255, 255, 255, 0.5);
}

.call-settings {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 6px;
}

.barphone {
  height: 100%;
  border-radius: 5px;

  &.active {
    animation: blink 1.5s ease-in-out infinite;
  }

  span {
    display: block;
  }

  .phone-container {
    display: flex;
  }

  &__phone {
    color: $subMenuActiveText;
    cursor: pointer !important;

    .el-button {
      font-size: 1.2rem;
    }

    .logged-ext-on-call {
      color: #ffffff;
      font-weight: 700;
    }

    .logged-ext-not-on-call {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  &__actions {
    display: flex;

    .actions-call {
      display: flex;
      justify-content: space-around;
      align-items: center;
      min-width: 140px;
    }
  }

  i {
    display: block;
  }

  .label-menu-item {
    font-size: 0.5rem;
  }

  .mdi-phone-hangup {
    color: #e95c5c;
  }
  .mdi-phone-paused {
    color: #e48fe4;
  }
  .mdi-phone-forward {
    color: rgb(241, 162, 14);
  }
  .mdi-call-merge {
    color: #00cd1d;
    margin-left: -4px;
    height: 24px;

    svg {
      height: 28px;
      width: 28px;
      margin-top: -2px;
    }
  }
}

.disabled {
  opacity: 0.4;
}

@keyframes blink {
  from {
    background-color: rgb(255, 152, 68);
  }
  50% {
    background-color: rgba(241, 97, 14, 0);
  }
  to {
    background-color: rgb(255, 152, 68);
  }
}
</style>
